import type {
  ImpactConfigMode,
  ImpactType,
  ImpactUnit,
  OffsetSettingType,
  SupportedCurrencyCode,
} from "@s2z-platform/constants";
import type { ApiStore, ApiStoreOrder } from "../api";
import type { ISO8601DateString } from "../../common";

type TCurrencyEntity = {
  code: string;
  name: string;
  symbol: string;
  rounding: number;
  decimalDigits: number;
  rateLocked: boolean;
};

type TCurrencyRateEntity = {
  id: number;
  fromCurrency: TCurrencyEntity;
  toCurrency: TCurrencyEntity;
  rate: number;
  createdAt: Date;
  updatedAt: Date;
};

/** @deprecated Use TStoreConfigPercentageRange instead */
export type OffsetPercentageRange = {
  offsetSetting: TOffsetSettingEntity;
  amountFrom: number;
  amountTo: number;
  percentage: number;
  uuid: string;
};

/** @deprecated Use TStoreImpactConfigEntity instead */
export type TOffsetSettingEntity = {
  id: number;
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
  store: ApiStore; // StoreEntity;
  type: OffsetSettingType;
  offsetPercentageRanges: OffsetPercentageRange[];
  percentagePerOrder?: number;
  isPercentagePerRange?: boolean;
  treePerOrder?: number;
  isTreePerBatch?: boolean;
  treePerBatchOrderAmount?: number;
  treePerBatchNumberOfTree?: number;
  plantTreeAlways?: boolean;
  avgCheckoutValue?: number;
  avgCheckoutVolume?: number;
  currency?: string;
  updateSchedule?: "legacy" | "live-update";
  runningTotalTrees?: number;
  runningTotalAmount?: number;
  currentMonthTreePrice?: number;
  sendCustomerEmails: boolean;
};

export type TStoreConfigPercentageRange = {
  amountFrom: number;
  amountTo: number | null;
  percentage: number;
  uuid: string;
};

export type TStoreImpactConfigEntity = {
  id: number;
  uuid: string;
  createdAt: Date;
  updatedAt: Date;
  type: ImpactType;
  mode: ImpactConfigMode;
  unit: string;
  version: number;
  percentageRanges: TStoreConfigPercentageRange[];
  percentagePerOrder?: number;
  isPercentagePerRange?: boolean;
  impactPerOrder?: number;
  isImpactPerBatch?: boolean;
  impactPerBatchAmount?: number;
  impactPerBatchCount?: number;
  minImpactPerBatch?: number;
  minOrderValue?: number;
};

/** @deprecated Legacy shape stored in old offset records */
export type LegacyCalculationDetails = {
  offsetSetting: TOffsetSettingEntity;
  treeCost?: number;
  currencyRate?: TCurrencyRateEntity;
};

/** V1 shape stored in new offset records */
export type CalculationDetailsV1 = {
  impactConfig: TStoreImpactConfigEntity;
  impactCost: number;
  /** Track conversion of user's sub currency to impact product price (fixed currency) */
  impactConversionRate?: TCurrencyRateEntity;
  /** Track conversion of user's store currency (what users pay) to impact product price (fixed currency) */
  orderConversionRate?: TCurrencyRateEntity;
};

export type CalculationDetailsJSON =
  | LegacyCalculationDetails
  | CalculationDetailsV1;

export function isLegacyDetails(
  d: CalculationDetailsJSON,
): d is LegacyCalculationDetails {
  return "offsetSetting" in d;
}

export interface ApiImpactOrder {
  type: ImpactType;
  unit: ImpactUnit;
  amount: number | null;
  impacts: number | null;
  isVoid: boolean;
  readyForUsage: boolean;
  addedToUsage: boolean;
  isPaid: boolean;
  storeOrder?: ApiStoreOrder;
  calculationDetails: CalculationDetailsJSON;
  currency: SupportedCurrencyCode;
  createdAt: ISO8601DateString;
  updatedAt: ISO8601DateString;
}
